// Accessibility Utilities
// Improve profile accessibility for all users

/**
 * Announce content changes to screen readers
 */
export const announceChange = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Generate ARIA labels for profile sections
 */
export const getAriaLabels = (profile) => {
  return {
    profilePicture: `${profile.firstName} ${profile.lastName}'s profile picture`,
    coverPhoto: 'Profile cover photo',
    editButton: 'Edit profile',
    shareButton: 'Share profile',
    followButton: profile.isFollowing ? 'Unfollow' : 'Follow',
    messageButton: `Send message to ${profile.firstName}`,
    skillEndorse: (skill) => `Endorse ${skill.name} skill`,
    mediaGallery: 'Media gallery with photos and videos',
    privacyBadge: (level) => `Privacy level: ${level}`,
    completeness: `Profile ${profile.profileCompleteness}% complete`,
    analytics: 'Profile analytics and insights',
    availability: `Availability: ${profile.availability?.status || 'Not specified'}`
  };
};

/**
 * Keyboard navigation helper
 */
export const handleKeyboardNavigation = (e, callbacks) => {
  const { onEnter, onSpace, onEscape, onArrowLeft, onArrowRight, onArrowUp, onArrowDown } = callbacks;

  switch (e.key) {
    case 'Enter':
      if (onEnter) {
        e.preventDefault();
        onEnter(e);
      }
      break;
    
    case ' ':
    case 'Space':
      if (onSpace) {
        e.preventDefault();
        onSpace(e);
      }
      break;
    
    case 'Escape':
      if (onEscape) {
        e.preventDefault();
        onEscape(e);
      }
      break;
    
    case 'ArrowLeft':
      if (onArrowLeft) {
        e.preventDefault();
        onArrowLeft(e);
      }
      break;
    
    case 'ArrowRight':
      if (onArrowRight) {
        e.preventDefault();
        onArrowRight(e);
      }
      break;
    
    case 'ArrowUp':
      if (onArrowUp) {
        e.preventDefault();
        onArrowUp(e);
      }
      break;
    
    case 'ArrowDown':
      if (onArrowDown) {
        e.preventDefault();
        onArrowDown(e);
      }
      break;
    
    default:
      break;
  }
};

/**
 * Focus trap for modals
 */
export class FocusTrap {
  constructor(element) {
    this.element = element;
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  activate() {
    // Find all focusable elements
    this.focusableElements = this.element.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
    );
    
    if (this.focusableElements.length === 0) return;
    
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    
    // Add event listener
    this.element.addEventListener('keydown', this.handleKeyDown);
    
    // Focus first element
    this.firstFocusableElement.focus();
  }

  deactivate() {
    this.element.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
}

/**
 * Get readable time for screen readers
 */
export const getReadableTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Skip to content link helper
 */
export const addSkipToContent = () => {
  const skipLink = document.createElement('a');
  skipLink.href = '#main-content';
  skipLink.className = 'skip-to-content';
  skipLink.textContent = 'Skip to main content';
  
  skipLink.addEventListener('click', (e) => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView();
    }
  });
  
  document.body.insertBefore(skipLink, document.body.firstChild);
};

/**
 * High contrast mode detector
 */
export const isHighContrastMode = () => {
  if (window.matchMedia) {
    return window.matchMedia('(prefers-contrast: high)').matches;
  }
  return false;
};

/**
 * Reduced motion detector
 */
export const prefersReducedMotion = () => {
  if (window.matchMedia) {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return false;
};

/**
 * Generate descriptive text for complex UI elements
 */
export const getDescriptiveText = {
  skillLevel: (level) => {
    const descriptions = {
      1: 'Beginner level',
      2: 'Beginner level',
      3: 'Elementary level',
      4: 'Elementary level',
      5: 'Intermediate level',
      6: 'Intermediate level',
      7: 'Advanced level',
      8: 'Advanced level',
      9: 'Expert level',
      10: 'Master level'
    };
    return descriptions[level] || `Level ${level} out of 10`;
  },
  
  profileCompleteness: (percentage) => {
    if (percentage === 100) return 'Profile is complete';
    if (percentage >= 80) return 'Profile is nearly complete';
    if (percentage >= 60) return 'Profile is mostly complete';
    if (percentage >= 40) return 'Profile is partially complete';
    if (percentage >= 20) return 'Profile has basic information';
    return 'Profile just started';
  },
  
  privacyLevel: (level) => {
    const descriptions = {
      public: 'Visible to everyone',
      clubs: 'Visible to club members only',
      private: 'Visible to you only'
    };
    return descriptions[level] || level;
  }
};

export default {
  announceChange,
  getAriaLabels,
  handleKeyboardNavigation,
  FocusTrap,
  getReadableTime,
  addSkipToContent,
  isHighContrastMode,
  prefersReducedMotion,
  getDescriptiveText
};
